import  { memo } from "react"
import { ExpandLess, ExpandMore } from "@mui/icons-material"
import { Box, IconButton, Tooltip } from "@material-ui/core"
import {
  activeBadgeStyle,
  borderRadiusBoxes,
  modelBadgeSx,
  themePalette,
} from "./styles"

export const PARENT_ROW_ESTIMATED_HEIGHT = 48
export const CHILD_ROW_ESTIMATED_HEIGHT = 42

// Badge minimo para estado activo/inactivo.
// Esta separado porque se reutiliza en padre e hijos y conviene memoizarlo.
const SelectionBadge = memo(function SelectionBadge({ active }) {
  return (
    <Tooltip title={active ? "Dispositivo habilitado" : "Dispositivo deshabilitado"}>
      <span style={activeBadgeStyle(active)}>
        <span
          style={{
            width: "0.375rem",
            height: "0.375rem",
            borderRadius: "9999px",
            backgroundColor: active ? themePalette.status.success : themePalette.error.main,
          }}
        />
        {active ? "Activo" : "Inactivo"}
      </span>
    </Tooltip>
  )
})

// Tarjeta visual de un medidor y sus hijos visibles.
// Existe para desacoplar la presentacion del LeftPanel y mantener la virtualizacion mas legible.
function MeterCard({
  meter,
  visibleChildren,
  isExpanded,
  isFilteringDevices,
  isMobile,
  selectedMeterId,
  onSelectMeter,
  onToggleExpanded,
  renderCloudAccessBadge,
  renderAssignmentNodeIcon,
  getStatusIcon,
}) {
  const hasChildren = Boolean(meter.children?.length)

  return (
    <Box
      sx={{
        border: `1px solid ${themePalette.border.subtle}`,
        borderRadius: borderRadiusBoxes,
        backgroundColor: themePalette.common.white,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0.4rem 0.75rem",
          minHeight: `${PARENT_ROW_ESTIMATED_HEIGHT}px`,
          cursor: "pointer",
          backgroundColor: selectedMeterId === meter.id ? themePalette.surface.selected : themePalette.common.white,
          boxSizing: "border-box",
        }}
        onClick={() => onSelectMeter(meter)}
      >
        <Box sx={{ minWidth: 0, flex: 1, display: "flex", alignItems: "center", gap: "0.55rem", flexWrap: "nowrap", overflow: "hidden" }}>
          <span
            style={{
              ...modelBadgeSx,
              flexShrink: 0,
            }}
          >
            {meter.model}
          </span>
          <span
            style={{
              fontSize: "1rem",
              color: themePalette.primary.main,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {meter.name} {meter.key ? <span style={{ color: themePalette.text.contrast }}>({meter.key})</span> : ""}
          </span>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          {renderCloudAccessBadge(meter, { compact: isMobile, listLabel: true, listIconOnly: true })}
          {!hasChildren && <SelectionBadge active={meter.active} />}
          {hasChildren ? (
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation()
                onToggleExpanded(meter.id)
              }}
            >
              {(isFilteringDevices || isExpanded) ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </IconButton>
          ) : (
            <>
              {renderAssignmentNodeIcon(meter)}
              {getStatusIcon(meter.productName || meter.model, meter.active, meter.online)}
            </>
          )}
        </Box>
      </Box>

      {visibleChildren.length > 0 && (
        <Box sx={{ backgroundColor: themePalette.surface.alt, borderTop: `1px solid ${themePalette.border.subtle}` }}>
          {visibleChildren.map((child, index) => (
            <Box
              key={child.id}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "0.32rem 0.75rem",
                paddingLeft: "3rem",
                minHeight: `${CHILD_ROW_ESTIMATED_HEIGHT}px`,
                cursor: "pointer",
                backgroundColor: selectedMeterId === child.id ? themePalette.surface.selectedChild : themePalette.surface.alt,
                borderTop: index === 0 ? "none" : `1px solid ${themePalette.border.subtle}`,
                boxSizing: "border-box",
              }}
              onClick={() => onSelectMeter(child)}
            >
              <Box sx={{ minWidth: 0, flex: 1, display: "flex", alignItems: "center", gap: "0.55rem", flexWrap: "nowrap", overflow: "hidden" }}>
                <span
                  style={{
                    fontSize: "1rem",
                    color: themePalette.primary.main,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {child.name} {child.key ? <span style={{ color: themePalette.text.contrast }}>({child.key})</span> : ""}
                </span>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <SelectionBadge active={child.active} />
                {renderAssignmentNodeIcon(child)}
                {getStatusIcon(child.productName || child.model, child.active, child.online)}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}

export default memo(MeterCard)
